"use client";

import { useMemo } from "react";
import NextImage from "next/image";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { cn } from "@/lib/utils";
import { GROQImage, GROQImageLoader } from "@/sanity/lib/definitions";

const LOGO_HEIGHT_REM = 3.5;

interface ScrollingPartnersLogosProps {
  logos: GROQImage[];
  label?: string;
  className?: string;
  theme?: "dark" | "light" | "dark-tinted";
  /** CSS color that matches the section background, used for the edge fade. */
  fadeColor?: string;
  /** Logo height in rem. Defaults to 3.5rem. */
  logoHeightRem?: number;
}

export default function ScrollingPartnersLogos({
  logos,
  label,
  className,
  theme = "light",
  fadeColor,
  logoHeightRem,
}: ScrollingPartnersLogosProps) {
  const autoScroll = useMemo(() => AutoScroll({ speed: 1.5, startDelay: 0, stopOnInteraction: false }), []);

  if (!logos?.length) return null;

  const repeated = [...logos, ...logos, ...logos];
  const color = fadeColor ?? (theme === "dark" ? "#001b14" : theme === "dark-tinted" ? "#ffffff" : "#ffffff");

  return (
    <div className={cn("w-full overflow-hidden", className)}>
      {label && (
        <p
          className={cn(
            "mb-0 pb-[2.5rem] pt-[3.5rem] text-center font-geist-mono text-[1.5rem] font-medium uppercase leading-[1.5] tracking-[0.06rem]",
            theme === "dark" ? "text-white/50" : theme === "dark-tinted" ? "text-black/60" : "text-black/40",
          )}>
          {label}
        </p>
      )}

      <div className="relative overflow-hidden py-[3.5rem]">
        {/* Left edge fade */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-[-2rem] z-10 hidden w-[25rem] blur-[1.5rem] lg:block"
          style={{
            background: `linear-gradient(to right, ${color} 50%, transparent 100%)`,
          }}
        />
        {/* Right edge fade */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-[-2rem] z-10 hidden w-[25rem] blur-[1.5rem] lg:block"
          style={{
            background: `linear-gradient(to left, ${color} 50%, transparent 100%)`,
          }}
        />

        <Carousel
          opts={{ loop: true }}
          plugins={[autoScroll]}
          className="w-full">
          <CarouselContent className="-ml-[11.25rem]">
            {repeated.map((logo, i) => {
              const h = logoHeightRem ?? LOGO_HEIGHT_REM;
              const w = logo.width && logo.height ? (logo.width / logo.height) * h : 8;
              return (
                <CarouselItem
                  key={i}
                  className="basis-auto pl-[11.25rem]">
                  <div
                    className={cn("relative shrink-0", theme === "dark" && "mix-blend-luminosity opacity-60")}
                    style={{
                      height: `${h}rem`,
                      width: `${w}rem`,
                      ...(theme === "light" && {
                        filter: "grayscale(1) opacity(0.45)",
                      }),
                      ...(theme === "dark-tinted" && {
                        filter: "grayscale(1) brightness(0.4) opacity(0.75)",
                      }),
                    }}>
                    <NextImage
                      src={logo.src}
                      alt={logo.name ?? ""}
                      fill
                      sizes={`${w}rem`}
                      loader={GROQImageLoader}
                      className="object-contain"
                    />
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
}
